// ============================================================================
// FinanceVisuals (E2, F-519) — vue « Visuels » du detail : KPI + repartitions + tendance
// ============================================================================
// Portage du PoC components/finance/finance-visuals.tsx. Purement PRESENTATIONNEL :
// recoit toutes ses donnees en props, ne fetch jamais (la page tient la query).
//
// Les depenses sont agregees par GROUPE (groupExpenseLines, lib portee) pour les
// cartes de repartition : ~4 parts lisibles au lieu de ~23 lignes ; les revenus
// restent par departement.

import type { TFunction } from 'i18next';
import type { Statement } from '../../lib/finance/computations';
import { groupExpenseLines } from '../../lib/finance/computations';
import type { FinanceSummary, FinanceTrend } from '../../hooks/useFinance';
import { FinanceKpiCards } from './FinanceKpiCards';
import { CategoryBreakdownCard } from './CategoryBreakdownCard';
import { BudgetComparisonCard } from './BudgetComparisonCard';
import { FinanceTrendCard } from './FinanceTrendCard';

export interface FinanceVisualsProps {
  statement: Statement;
  summary: FinanceSummary;
  /** null = tendance non chargee (vue tableau, M-507) — la carte est alors omise. */
  trend: FinanceTrend | null;
  locale: string;
  t: TFunction;
}

export function FinanceVisuals({ statement, summary, trend, locale, t }: FinanceVisualsProps) {
  const expenseByGroup = groupExpenseLines(statement.expenseLines);

  return (
    <div className="space-y-5">
      <FinanceKpiCards statement={statement} summary={summary} locale={locale} t={t} />

      <section className="space-y-2">
        <h2 className="text-lead font-semibold text-slate-800 dark:text-slate-100">
          {t('gest:finance.breakdown.sectionTitle')}
        </h2>
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:finance.breakdown.sectionDescription')}
        </p>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <CategoryBreakdownCard
            title={t('gest:finance.table.revenues')}
            section="revenue"
            lines={statement.revenueLines}
            total={statement.totalRevenue}
            locale={locale}
            t={t}
          />
          <CategoryBreakdownCard
            title={t('gest:finance.table.expenses')}
            section="expense"
            lines={expenseByGroup}
            total={statement.totalExpense}
            locale={locale}
            t={t}
          />
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-lead font-semibold text-slate-800 dark:text-slate-100">
          {t('gest:finance.budgetCompare.sectionTitle')}
        </h2>
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:finance.budgetCompare.sectionDescription')}
        </p>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <BudgetComparisonCard
            title={t('gest:finance.table.revenues')}
            section="revenue"
            lines={statement.revenueLines}
            locale={locale}
            t={t}
          />
          <BudgetComparisonCard
            title={t('gest:finance.table.expenses')}
            section="expense"
            lines={expenseByGroup}
            locale={locale}
            t={t}
          />
        </div>
      </section>

      {trend ? <FinanceTrendCard trend={trend} locale={locale} t={t} /> : null}
    </div>
  );
}
